import {
  Calculation, CalculationRowType, TaxAtSourceUsecase, UserDefinedRow,
  BicycleBenefitUsecase, BoardUsecase, CarBenefitUsecase, DailyAllowanceUsecase, EmploymentTerminationUsecase,
  ForeclosureUsecase, HourlySalaryWithWorkingTimeCompensationUsecase, IrIncomeTypeUsecase, MealBenefitUsecase,
  NonProfitOrgUsecase, OtherCompensationUsecase, PaidSickLeaveUsecase, RemunerationUsecase, SubsidisedCommuteUsecase, TaxWithholdingUsecase, TotalWorkerPaymentUsecase,
  TrainingUsecase, UnionPaymentUsecase, WorkingTimeCompensationUsecase, AbsencePeriodUsecase,
  TimeRatePayKind,
  BoardKind,
  AgeGroupCode,
  WorkingTimeCompensationPricing,
  PayStatsUsecase
} from "../model";

import { BaseUsecaseLogic } from "./BaseUsecaseLogic";
import { RowUsecaseCalcData } from "./RowUsecaseCalcData";
import { UsecaseRow } from "./UsecaseRow";
import { UsecaseValidationMessage } from "./UsecaseValidationMessage";
import { Dates } from "../util";
import {
  AbsencePeriodUsecaseLogic, BicycleBenefitUsecaseLogic, BoardUsecaseLogic, CarBenefitUsecaseLogic, DailyAllowanceUsecaseLogic, DefaultUsecaseLogic, EmploymentTerminationUsecaseLogic, ExpensesUsecaseLogic,
  ForeclosureUsecaseLogic, HourlySalaryWithWorkingTimeCompensationUsecaseLogic, IrIncomeTypeUsecaseLogic, MealBenefitUsecaseLogic, MilageOwnCarUsecaseLogic,
  NonProfitOrgUsecaseLogic, OtherCompensationUsecaseLogic, PaidSickLeaveUsecaseLogic, RemunerationUsecaseLogic, SubsidisedCommuteUsecaseLogic, TaxAtSourceUsecaseLogic, TaxWithholdingUsecaseLogic, TotalWorkerPaymentUsecaseLogic,
  TrainingUsecaseLogic, UnionPaymentUsecaseLogic, WorkingTimeCompensationUsecaseLogic, HolidaysUsecaseLogic, PayStatsUsecaseLogic, TimeRatePayUsecaseLogic
} from "./rows";
import type { ExpensesUsecase } from "./ExpensesUsecase";
import { MilageOwnCarUsecase } from "./MilageOwnCarUsecase";
import { ExpensesVatRate } from "../logic";

/**
 * Provides logic for usecase user interfaces based on calculation rows.
 */
export class RowsUsecaseLogic {

  /**
   * Gets a blank UserDefinedRow with typed usecase data and usecase specific default data.
   * @param type Type of the usecase for the row.
   */
  public static getBlankUsecaseRow(type: CalculationRowType.AbsencePeriod): UsecaseRow<CalculationRowType.AbsencePeriod, AbsencePeriodUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.BicycleBenefit): UsecaseRow<CalculationRowType.BicycleBenefit, BicycleBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Board): UsecaseRow<CalculationRowType.Board, BoardUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.CarBenefit): UsecaseRow<CalculationRowType.CarBenefit, CarBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.DailyAllowance): UsecaseRow<CalculationRowType.DailyAllowance, DailyAllowanceUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.EmploymentTermination): UsecaseRow<CalculationRowType.EmploymentTermination, EmploymentTerminationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Expenses): UsecaseRow<CalculationRowType.Expenses, ExpensesUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Foreclosure): UsecaseRow<CalculationRowType.Foreclosure, ForeclosureUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.HourlySalaryWithWorkingTimeCompensation): UsecaseRow<CalculationRowType.HourlySalaryWithWorkingTimeCompensation, HourlySalaryWithWorkingTimeCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.IrIncomeType): UsecaseRow<CalculationRowType.IrIncomeType, IrIncomeTypeUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.MealBenefit): UsecaseRow<CalculationRowType.MealBenefit, MealBenefitUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.MilageOwnCar): UsecaseRow<CalculationRowType.MilageOwnCar, MilageOwnCarUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.NonProfitOrg): UsecaseRow<CalculationRowType.NonProfitOrg, NonProfitOrgUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.OtherCompensation): UsecaseRow<CalculationRowType.OtherCompensation, OtherCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.PaidSickLeave): UsecaseRow<CalculationRowType.PaidSickLeave, PaidSickLeaveUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.PrepaidExpenses): UsecaseRow<CalculationRowType.PrepaidExpenses, ExpensesUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Remuneration): UsecaseRow<CalculationRowType.Remuneration, RemunerationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.SubsidisedCommute): UsecaseRow<CalculationRowType.SubsidisedCommute, SubsidisedCommuteUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TotalWorkerPayment): UsecaseRow<CalculationRowType.TotalWorkerPayment, TotalWorkerPaymentUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.Training): UsecaseRow<CalculationRowType.Training, TrainingUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.UnionPayment): UsecaseRow<CalculationRowType.UnionPayment, UnionPaymentUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.WorkingTimeCompensation): UsecaseRow<CalculationRowType.WorkingTimeCompensation, WorkingTimeCompensationUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.PayStats): UsecaseRow<CalculationRowType.PayStats, PayStatsUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TimeRatePay): UsecaseRow<CalculationRowType.TimeRatePay, ({ kind: TimeRatePayKind})>;
  public static getBlankUsecaseRow(type: CalculationRowType.TaxAtSource): UsecaseRow<CalculationRowType.TaxAtSource, TaxAtSourceUsecase>;
  public static getBlankUsecaseRow(type: CalculationRowType.TaxWithholding): UsecaseRow<CalculationRowType.TaxWithholding, TaxWithholdingUsecase>;
  public static getBlankUsecaseRow<T extends CalculationRowType, D extends { [key: string]: any }>(type: CalculationRowType): UsecaseRow<T, D>;
  /**
   * Gets a blank UserDefinedRow with typed usecase data and usecase specific default data.
   * @param type Type of the usecase for the row.
   */
  public static getBlankUsecaseRow(type: CalculationRowType): UsecaseRow<CalculationRowType, any> {
    const result = {
      rowType: type,
      count: 1,
      accounting: {
        dimensions: [],
      },
      data: {},
    };
    this.getUsecaseLogic(type)?.updateUsecase(result, "today", undefined);
    return result;
  }

  /**
   * Gets the usecase specific logic for a row type.
   * @param type Row type for which we should return the logic.
   * @returns Usecase logic for the row type.
   */
  public static getUsecaseLogic(type: CalculationRowType.AbsencePeriod): AbsencePeriodUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.BicycleBenefit): BicycleBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Board): BoardUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.CarBenefit): CarBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.DailyAllowance): DailyAllowanceUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.EmploymentTermination): EmploymentTerminationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Expenses): ExpensesUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Foreclosure): ForeclosureUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.HourlySalaryWithWorkingTimeCompensation): HourlySalaryWithWorkingTimeCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.IrIncomeType): IrIncomeTypeUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.MealBenefit): MealBenefitUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.MilageOwnCar): MilageOwnCarUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.NonProfitOrg): NonProfitOrgUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.OtherCompensation): OtherCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.PaidSickLeave): PaidSickLeaveUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.PrepaidExpenses): ExpensesUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Remuneration): RemunerationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.SubsidisedCommute): SubsidisedCommuteUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TotalWorkerPayment): TotalWorkerPaymentUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.Training): TrainingUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.UnionPayment): UnionPaymentUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.WorkingTimeCompensation): WorkingTimeCompensationUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.PayStats): PayStatsUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TimeRatePay): TimeRatePayUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TaxAtSource): TaxAtSourceUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.TaxWithholding): TaxWithholdingUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType.HolidayBonus | CalculationRowType.HolidaySalary | CalculationRowType.HolidayCompensation): HolidaysUsecaseLogic;
  public static getUsecaseLogic(type: CalculationRowType): BaseUsecaseLogic;
  /**
   * Gets the usecase specific logic for a row type.
   * @param type Row type for which we should return the logic.
   * @returns Usecase logic for the row type.
   */
  public static getUsecaseLogic(type: CalculationRowType): BaseUsecaseLogic {
    switch (type) {
      case CalculationRowType.AbsencePeriod:
        return new AbsencePeriodUsecaseLogic();
      case CalculationRowType.BicycleBenefit:
        return new BicycleBenefitUsecaseLogic();
      case CalculationRowType.Board:
        return new BoardUsecaseLogic();
      case CalculationRowType.CarBenefit:
        return new CarBenefitUsecaseLogic();
      case CalculationRowType.DailyAllowance:
        return new DailyAllowanceUsecaseLogic();
      case CalculationRowType.EmploymentTermination:
        return new EmploymentTerminationUsecaseLogic();
      case CalculationRowType.Expenses:
        return new ExpensesUsecaseLogic();
      case CalculationRowType.Foreclosure:
        return new ForeclosureUsecaseLogic();
      case CalculationRowType.HourlySalaryWithWorkingTimeCompensation:
        return new HourlySalaryWithWorkingTimeCompensationUsecaseLogic();
      case CalculationRowType.IrIncomeType:
        return new IrIncomeTypeUsecaseLogic();
      case CalculationRowType.MealBenefit:
        return new MealBenefitUsecaseLogic();
      case CalculationRowType.MilageOwnCar:
        return new MilageOwnCarUsecaseLogic();
      case CalculationRowType.NonProfitOrg:
        return new NonProfitOrgUsecaseLogic();
      case CalculationRowType.OtherCompensation:
        return new OtherCompensationUsecaseLogic();
      case CalculationRowType.PaidSickLeave:
        return new PaidSickLeaveUsecaseLogic();
      case CalculationRowType.PrepaidExpenses:
        return new ExpensesUsecaseLogic();
      case CalculationRowType.Remuneration:
        return new RemunerationUsecaseLogic();
      case CalculationRowType.SubsidisedCommute:
        return new SubsidisedCommuteUsecaseLogic();
      case CalculationRowType.TotalWorkerPayment:
        return new TotalWorkerPaymentUsecaseLogic();
      case CalculationRowType.Training:
        return new TrainingUsecaseLogic();
      case CalculationRowType.UnionPayment:
        return new UnionPaymentUsecaseLogic();
      case CalculationRowType.WorkingTimeCompensation:
        return new WorkingTimeCompensationUsecaseLogic();
      case CalculationRowType.PayStats:
        return new PayStatsUsecaseLogic();
      case CalculationRowType.TimeRatePay:
        return new TimeRatePayUsecaseLogic();
      case CalculationRowType.TaxAtSource:
        return new TaxAtSourceUsecaseLogic();
      case CalculationRowType.TaxWithholding:
        return new TaxWithholdingUsecaseLogic();
      case CalculationRowType.HolidayBonus:
      case CalculationRowType.HolidaySalary:
      case CalculationRowType.HolidayCompensation:
        return new HolidaysUsecaseLogic();
      default:
        return new DefaultUsecaseLogic();
    }
  }

  /**
   * Returns the grouping filter for a row type.
   * In the typical case each row in the group has the same rowType, type, kind, price, unit and message.
   * Rows in a group can be merged together.
   * @param type Row type for the grouping filter.
   * @param arr Array of rows to group. This is required for some grouping filters especially when there is no reason for grouping but each object should be in its own group.
   * @returns Filter for grouping the rows.
   */
  public static getGroupingFilter(type: CalculationRowType, arr: UserDefinedRow[]):  ((r: UserDefinedRow) => string){
    switch (type) {
      case CalculationRowType.AbsencePeriod:
        return x => this.getRowKey(x,[x.data?.isPaid, x.data?.absenceCustomCode, x.data?.isHolidayAccrual], true) ;
      case CalculationRowType.BicycleBenefit:
        return x => this.getRowId(x, arr);
      case CalculationRowType.Board:
        return x => this.getRowKey(x);
      case CalculationRowType.CarBenefit:
        return x => this.getRowId(x, arr);
      case CalculationRowType.DailyAllowance:
        return x => this.getRowKey(x,[x.data?.country, x.data?.isHalf]) ;
      case CalculationRowType.EmploymentTermination:
        return x => this.getRowKey(x);
      case CalculationRowType.Expenses:
        return x => this.getRowId(x, arr);
      case CalculationRowType.Foreclosure:
        return x => this.getRowId(x, arr);
      case CalculationRowType.HourlySalaryWithWorkingTimeCompensation:
        return x => {
          if ((x.data?.compensationRows?.length ?? 0) != 1 ) {
            return this.getRowId(x, arr);
          }
          const compensationRow = { ...x.data?.compensationRows[0], count: undefined };
          return this.getRowKey(x, [JSON.stringify(compensationRow), x.data?.pricing]);
        };
      case CalculationRowType.IrIncomeType:
        return x => this.getRowKey(x, [x.data?.unit, JSON.stringify(x.data?.irData)]) ;
      case CalculationRowType.MealBenefit:
        return x => this.getRowId(x, arr);
      case CalculationRowType.MilageOwnCar:
        return x => this.getRowKey(x, [x.data?.vehicleType, x.data?.passengerCount, x.data?.additionsAllowed, x.data?.passengersAllowed, JSON.stringify(x.data?.additions)]);
      case CalculationRowType.NonProfitOrg:
        return x => this.getRowKey(x);
      case CalculationRowType.OtherCompensation:
        return x => this.getRowKey(x);
      case CalculationRowType.PaidSickLeave:
        return x => this.getRowKey(x);
      case CalculationRowType.PrepaidExpenses:
        return x => this.getRowId(x, arr);
      case CalculationRowType.Remuneration:
        return x => this.getRowKey(x);
      case CalculationRowType.SubsidisedCommute:
        return x => this.getRowId(x, arr);
      case CalculationRowType.TotalWorkerPayment:
        return x => this.getRowId(x, arr);
      case CalculationRowType.Training:
        return x => this.getRowKey(x);
      case CalculationRowType.UnionPayment:
        return x => this.getRowId(x, arr);
      case CalculationRowType.WorkingTimeCompensation:
        return x => this.getRowKey(x);
      case CalculationRowType.PayStats:
        return x => this.getRowKey(x, arr);
      case CalculationRowType.TaxAtSource:
        return x => this.getRowId(x, arr);
      case CalculationRowType.TaxWithholding:
        return x => this.getRowId(x, arr);
      case CalculationRowType.HolidayBonus:
      case CalculationRowType.HolidaySalary:
      case CalculationRowType.HolidayCompensation:
        return x => this.getRowKey(x);
      default:
        return x => this.getRowKey(x);
    }
  }

  /**
   * Returns usecase logic for a row and calculation.
   * @param row Row that the logic operates on.
   * @param calc Calculation that contains the row (used for date, potentially other things).
   * @param doUpdate If true, will run first updateUsecase on the row.
   */
  public static getUsecaseLogicForCalc(row: UserDefinedRow, calc: Calculation, doUpdate = false): BaseUsecaseLogic {
    row.rowType ??= CalculationRowType.Unknown;
    const logic = RowsUsecaseLogic.getUsecaseLogic(row.rowType);
    if (doUpdate) {
      logic.updateUsecase(row, calc?.workflow?.salaryDate ?? "today", RowUsecaseCalcData.fromCalc(calc));
    }
    return logic;
  }

  /**
   * Returns a validation message for a row or null if none is provided.
   * Does the common validations (price and count) and then runs usecase specific validations.
   * @param row The row that is validated.
   */
  public static getValidation(row: UserDefinedRow): UsecaseValidationMessage | undefined {
    row.rowType ??= CalculationRowType.Unknown;
    return RowsUsecaseLogic.getUsecaseLogic(row.rowType).getValidation(row);
  }

  /**
   * Returns example data for a specific calculation row type and optional kind.
   * Used to generate sample data for documentation and testing purposes.
   * @param rowType The type of calculation row to get example data for
   * @param kind Optional kind parameter to get specific variant of example data
   * @returns Example data object with default values for the specified row type
   */
  public static getUsecaseExampleData(rowType: CalculationRowType, kind?: string): any {
    const examples = {
      [CalculationRowType.AbsencePeriod]: {
        kind: "undefined",
        isPaid: true,
        isHolidayAccrual: true,
      } as AbsencePeriodUsecase,
      [CalculationRowType.BicycleBenefit]: {
        kind: "undefined",
        deduction: 100,
        taxable: 100,
        periodDivider: 1,
      } as BicycleBenefitUsecase,
      [CalculationRowType.Board]: {
        kind: BoardKind.Undefined
      } as BoardUsecase,
      [CalculationRowType.CarBenefit]: {
        ageGroup: AgeGroupCode.A,
        deduction: 100,
        kind: "undefined",
        emissionsValue: 100,
        isKilometersBased: true,
        kilometers: 100,
      } as CarBenefitUsecase,
      [CalculationRowType.DailyAllowance]: {
        kind: "undefined",
        country: "undefined",
        isHalf: false,
      } as DailyAllowanceUsecase,
      [CalculationRowType.EmploymentTermination]: {
        kind: "undefined",
      } as EmploymentTerminationUsecase,
      [CalculationRowType.Expenses]: {
        date: Dates.getToday(),
        vatRate: ExpensesVatRate.Tax255,
        total: 100,
      } as ExpensesUsecase,
      [CalculationRowType.Foreclosure]: {
        kind: "undefined",
        referenceNumber: "RF341012"
      } as ForeclosureUsecase,
      [CalculationRowType.HourlySalaryWithWorkingTimeCompensation]: {
        compensationRows: [
          {
            count: null,
            percent: 0.50
          }
        ],
        kind: "undefined",
        pricing: WorkingTimeCompensationPricing.Percent
      } as HourlySalaryWithWorkingTimeCompensationUsecase,
      [CalculationRowType.IrIncomeType]: {
        irData: {},
        kind: "unknown"
      } as IrIncomeTypeUsecase,
      [CalculationRowType.MealBenefit]: {
        kind: "undefined",
        deduction: 100,
        taxable: 100,
        periodDivider: 1,
      } as MealBenefitUsecase,
      [CalculationRowType.MilageOwnCar]: {
        additionsAllowed: true,
        passengersAllowed: true,
        vehicleType: "car",
        additions: null,
      } as unknown as MilageOwnCarUsecase,
      [CalculationRowType.NonProfitOrg]: {
        kind: "undefined"
      } as NonProfitOrgUsecase,
      [CalculationRowType.OtherCompensation]: {
        kind: "undefined",
      } as OtherCompensationUsecase,
      [CalculationRowType.PaidSickLeave]: {
        kind: "undefined",
      } as PaidSickLeaveUsecase,
      [CalculationRowType.PrepaidExpenses]: {
        date: Dates.getToday(),
        vatRate: ExpensesVatRate.Tax255,
        total: 100,
      } as ExpensesUsecase,
      [CalculationRowType.Remuneration]: {
        kind: "undefined",
      } as RemunerationUsecase,
      [CalculationRowType.SubsidisedCommute]: {
        kind: "undefined",
        deduction: 100,
        taxable: 100
      } as SubsidisedCommuteUsecase,
      [CalculationRowType.TotalWorkerPayment]: {
        kind: "undefined",
        rowType
      } as TotalWorkerPaymentUsecase,
      [CalculationRowType.Training]: {
        kind: "undefined"
      } as TrainingUsecase,
      [CalculationRowType.UnionPayment]: {
        kind: "undefined",
        recipientFullName: "John Doe",
        iban: "FI1277124148815337",
        referenceNumber: "88104193810287782386"
      } as UnionPaymentUsecase,
      [CalculationRowType.WorkingTimeCompensation]: {
        kind: "undefined",
      } as WorkingTimeCompensationUsecase,
      [CalculationRowType.TaxAtSource]: {
        kind: "undefined",
        taxValue: 100,
        deductionPrice: 100,
        deductionCount: 1,
        deduction: 100,
        totalTaxable: 100,
      } as TaxAtSourceUsecase,
      [CalculationRowType.TaxWithholding]: {
        kind: "undefined",
      } as TaxWithholdingUsecase
    };
    const data = examples[rowType];
    if (kind) {
      data.kind = kind;
    }
    const blankRow = RowsUsecaseLogic.getBlankUsecaseRow(rowType);
    if(data) {
      blankRow.data = data;
    }
    this.getUsecaseLogic(rowType)?.updateUsecase(blankRow, "today", undefined);
    return blankRow.data;
  }


  private static getRowKey(row: UserDefinedRow, additionalFields: any[] = [], excludeMessage: boolean = false): string {
    const items = ["rowKey",row.rowType,row.data?.kind,this.getHarmonizedType(row),row.unit,row.price];
    items.push(...additionalFields);
    if (!excludeMessage) {
      items.push(row.message);
    }
    return items.join("-");
  }
  private static getRowId(row: UserDefinedRow, arr: UserDefinedRow[]): string {
    const items = ["rowId", arr.indexOf(row)];
    return items.join("-");
  }

  private static getHarmonizedType(row: UserDefinedRow): string {
    if (row?.type) {
      return row.type;
    }
    return `/builtIn/${row?.rowType}`;
  }
}
